// Ported from prototypes/shared/charts.js (the reviewed prototype).
import * as d3 from 'd3';
import E from './engine.js';
/* Chart renderers for prototypes (D3 v7 global). Each chart re-renders on resize,
   reads colors from CSS tokens, has a hover tooltip, and offers a table view. */
  const css = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const UNITS = { mi: false };
  const dist = (m) => (UNITS.mi ? m / 1609.344 : m / 1000);
  const du = () => (UNITS.mi ? 'mi' : 'km');
  // One rounding rule for every distance on the page.
  const fmtDist = (m, dp = 1) => `${dist(m).toLocaleString(undefined, { maximumFractionDigits: dp, minimumFractionDigits: dp })} ${du()}`;
  const fmtElev = (m) => (UNITS.mi ? `${Math.round(m * 3.28084).toLocaleString()} ft` : `${Math.round(m).toLocaleString()} m`);
  const paceFor = (s, m) => (UNITS.mi ? s / (m / 1609.344) : s / (m / 1000));
  const fmtPaceU = (s, m) => `${E.fmtPace(paceFor(s, m))} /${du()}`;
  const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fmtDate = (k) => { const d = E.fromKey(k); return `${d.getUTCDate()} ${MON[d.getUTCMonth()]}`; };
  const seq = () => [1, 2, 3, 4, 5, 6, 7].map((i) => css('--seq-' + i));
  // readable label colour on a filled mark
  const inkOn = (fill) => (d3.hsl(fill).l > 0.6 ? '#10151c' : '#ffffff');

  // ---------- tooltip ----------
  let tipEl;
  function tip(html, ev) {
    if (!tipEl) { tipEl = document.createElement('div'); tipEl.className = 'tip'; tipEl.setAttribute('role', 'status'); document.body.appendChild(tipEl); }
    if (!html) { tipEl.hidden = true; return; }
    tipEl.hidden = false; tipEl.innerHTML = html;
    const x = Math.min(window.innerWidth - 140, Math.max(140, ev.clientX));
    tipEl.style.left = x + 'px'; tipEl.style.top = ev.clientY + 'px';
  }
  function bindTip(sel, fn) {
    sel.on('pointerenter pointermove', (ev, d) => tip(fn(d), ev)).on('pointerleave', () => tip(null))
      .on('focus', (ev, d) => { const r = ev.target.getBoundingClientRect(); tip(fn(d), { clientX: r.x + r.width / 2, clientY: r.y }); })
      .on('blur', () => tip(null));
  }

  // ---------- frame helper ----------
  function frame(el, h, m = { t: 12, r: 12, b: 26, l: 36 }) {
    el.innerHTML = '';
    const w = Math.max(260, el.clientWidth || 600);
    const svg = d3.select(el).append('svg').attr('viewBox', `0 0 ${w} ${h}`).attr('role', 'img');
    return { svg, w, h, m, iw: w - m.l - m.r, ih: h - m.t - m.b, g: svg.append('g').attr('transform', `translate(${m.l},${m.t})`) };
  }
  function yGrid(g, y, iw, ticks, fmt) {
    const gg = g.append('g').attr('class', 'grid');
    y.ticks(ticks).forEach((t) => {
      gg.append('line').attr('x1', 0).attr('x2', iw).attr('y1', y(t)).attr('y2', y(t));
      gg.append('text').attr('x', -6).attr('y', y(t)).attr('dy', '0.32em').attr('text-anchor', 'end').text(fmt ? fmt(t) : t);
    });
  }
  // rounded data-end, square at baseline (vertical bars)
  function barPath(x, y, w, h, r = 4) {
    if (h <= 0) return '';
    r = Math.min(r, w / 2, h);
    return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
  }
  // month starts inside [start, end]; long ranges thin the labels and show years
  function monthStarts(startKey, endKey) {
    const s = E.fromKey(startKey), e = E.fromKey(endKey), out = [];
    for (let d = new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() + (s.getUTCDate() > 1 ? 1 : 0), 1)); d <= e; d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1))) out.push(d);
    return out;
  }
  function monthLabel(d, multiYear) { return multiYear && d.getUTCMonth() === 0 ? String(d.getUTCFullYear()) : MON[d.getUTCMonth()]; }
  // Month labels along a time axis, never closer than `gap` px: years (January) go down first on
  // multi-year spans (thinned to every 2nd, 5th… year when tight), then months fill the space left.
  function axisMonths(f, x, startKey, endKey, gap = 46) {
    const ms = monthStarts(startKey, endKey), multi = startKey.slice(0, 4) !== endKey.slice(0, 4);
    const placed = [];
    const fits = (px) => px >= 0 && px <= f.iw && placed.every((p) => Math.abs(p - px) >= gap);
    const put = (d) => { const px = x(d); if (!fits(px)) return; placed.push(px); f.g.append('text').attr('x', px).attr('y', f.ih + 16).attr('text-anchor', 'middle').text(monthLabel(d, multi)); };
    if (multi) {
      const jans = ms.filter((d) => d.getUTCMonth() === 0), yrPx = jans.length > 1 ? x(jans[1]) - x(jans[0]) : Infinity;
      const step = [1, 2, 5, 10].find((s) => yrPx * s >= gap) || 10;
      jans.filter((d) => d.getUTCFullYear() % step === 0).forEach(put);
    }
    const every = Math.max(1, Math.ceil(ms.length / Math.max(2, Math.floor(f.iw / gap))));
    if (!multi || every < 12) ms.forEach((d, i) => { if (i % every === 0 && !(multi && d.getUTCMonth() === 0)) put(d); });
  }
  const monthTicks = (f, x, rpt) => axisMonths(f, x, rpt.start, rpt.end, 46);

  // ---------- table view ----------
  function tableToggle(host, cols, rows, label = 'Show as table') {
    const btn = document.createElement('button'); btn.className = 'table-toggle'; btn.textContent = label; btn.type = 'button';
    const wrap = document.createElement('div'); wrap.className = 'table-wrap'; wrap.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    btn.addEventListener('click', () => {
      if (wrap.hidden && !wrap.firstChild) {
        const t = document.createElement('table'); t.className = 'data';
        t.innerHTML = `<thead><tr>${cols.map((c) => `<th class="${c.n ? 'n' : ''}">${c.h}</th>`).join('')}</tr></thead><tbody>${rows().map((r) => `<tr>${cols.map((c) => `<td class="${c.n ? 'n' : ''}">${c.f(r)}</td>`).join('')}</tr>`).join('')}</tbody>`;
        wrap.appendChild(t);
      }
      wrap.hidden = !wrap.hidden; btn.textContent = wrap.hidden ? label : 'Hide table'; btn.setAttribute('aria-expanded', String(!wrap.hidden));
    });
    host.appendChild(btn); host.appendChild(wrap);
  }

  // ---------- 1. calendar heatmap (horizontal; vertical weeks-as-rows on narrow screens) ----------
  const CAL_BINS = [3, 6, 9, 13, 18, 25];
  function calendar(el, rpt) {
    const end = E.fromKey(rpt.end);
    const start = E.daysBetween(E.fromKey(rpt.start), end) > 371 ? E.addDays(end, -363) : E.fromKey(rpt.start);
    const byDay = {};
    rpt.runs.forEach((r) => { (byDay[r.date] = byDay[r.date] || []).push(r); });
    const firstMon = E.addDays(start, -((start.getUTCDay() + 6) % 7));
    const cols = Math.ceil((E.daysBetween(firstMon, end) + 1) / 7);
    const w0 = Math.max(260, el.clientWidth || 600);
    const vertical = w0 < 520;
    const ramp = seq();
    const color = (km) => (km <= 0 ? css('--empty-cell') : ramp[CAL_BINS.filter((b) => km >= b).length]);
    const days = [];
    for (let d = start; d <= end; d = E.addDays(d, 1)) {
      const k = E.dayKey(d), rs = byDay[k] || [];
      days.push({ k, d, rs, km: rs.reduce((a, r) => a + r.distM, 0) / 1000, wk: Math.floor(E.daysBetween(firstMon, d) / 7), dow: (d.getUTCDay() + 6) % 7 });
    }
    let f, cell, step;
    const gap = 2;
    if (!vertical) {
      cell = Math.max(4, Math.min(22, Math.floor((w0 - 34) / cols) - gap)); step = cell + gap;
      f = frame(el, 7 * step + 22, { t: 18, r: 4, b: 4, l: 30 });
      days.forEach((x) => { x.cx = x.wk * step; x.cy = x.dow * step; });
      let lastM = -1;
      days.forEach((x) => { if (x.dow === 0 && x.d.getUTCMonth() !== lastM && x.d.getUTCDate() <= 7) { lastM = x.d.getUTCMonth(); f.g.append('text').attr('x', x.cx).attr('y', -6).text(MON[lastM]); } });
      [0, 2, 4, 6].forEach((r) => f.g.append('text').attr('x', -6).attr('y', r * step + cell / 2).attr('dy', '0.32em').attr('text-anchor', 'end').text(DOW[r][0]));
    } else {
      cell = Math.max(8, Math.min(16, Math.floor((w0 - 40) / 7) - gap)); step = cell + gap;
      f = frame(el, cols * step + 22, { t: 18, r: 4, b: 4, l: 34 });
      days.forEach((x) => { x.cx = x.dow * step; x.cy = x.wk * step; });
      DOW.forEach((d, i) => f.g.append('text').attr('x', i * step + cell / 2).attr('y', -6).attr('text-anchor', 'middle').text(d[0]));
      let lastM = -1;
      days.forEach((x) => { if (x.dow === 0 && x.d.getUTCMonth() !== lastM && x.d.getUTCDate() <= 7) { lastM = x.d.getUTCMonth(); f.g.append('text').attr('x', -6).attr('y', x.cy + cell / 2).attr('dy', '0.32em').attr('text-anchor', 'end').text(MON[lastM]); } });
    }
    const cells = f.g.selectAll('rect.c').data(days).join('rect').attr('class', 'c')
      .attr('x', (x) => x.cx).attr('y', (x) => x.cy).attr('width', cell).attr('height', cell).attr('rx', 2)
      .attr('fill', (x) => color(x.km)).attr('tabindex', (x) => (x.rs.length ? 0 : null));
    f.g.selectAll('rect.race').data(days.filter((x) => x.rs.some((r) => r.isRace))).join('rect').attr('class', 'race')
      .attr('x', (x) => x.cx - 1.5).attr('y', (x) => x.cy - 1.5).attr('width', cell + 3).attr('height', cell + 3).attr('rx', 3)
      .attr('fill', 'none').attr('stroke', css('--ink')).attr('stroke-width', 1.5).attr('pointer-events', 'none');
    f.svg.attr('aria-label', `Calendar of ${rpt.totals.activeDays} running days`);
    bindTip(cells, (x) => `${fmtDate(x.k)}<br>${x.rs.length ? x.rs.map((r) => `<b>${fmtDist(r.distM)}</b> ${r.isRace ? '· ' + r.raceName : r.indoor ? '· indoor' : ''}`).join('<br>') : 'No run'}`);
    return { legend: `<span>None</span><span class="ramp">${[css('--empty-cell'), ...ramp].map((c) => `<span style="background:${c}"></span>`).join('')}</span><span>${fmtDist(25000, 0)}+ a day</span><span style="margin-left:12px"><span class="sw" style="border:1.5px solid var(--ink);background:none"></span>Race</span>` };
  }

  // ---------- 2. weekly volume ----------
  function weekly(el, rpt) {
    const f = frame(el, 230, { t: 16, r: 10, b: 26, l: 36 });
    const weeks = rpt.weeks;
    const x = d3.scaleBand().domain(weeks.map((w) => w.week)).range([0, f.iw]).paddingInner(0.18);
    const maxKm = d3.max(weeks, (w) => dist(w.km * 1000)) || 1;
    const y = d3.scaleLinear().domain([0, maxKm * 1.08]).nice().range([f.ih, 0]);
    yGrid(f.g, y, f.iw, 4);
    rpt.breaks.forEach((b) => {
      const ws = weeks.filter((w) => E.dayKey(E.addDays(E.fromKey(w.week), 6)) >= b.first && w.week <= b.last);
      if (!ws.length) return;
      const x0 = x(ws[0].week), x1 = x(ws[ws.length - 1].week) + x.bandwidth();
      f.g.append('rect').attr('x', x0).attr('y', 0).attr('width', x1 - x0).attr('height', f.ih).attr('fill', css('--rule')).attr('opacity', 0.55);
      if (x1 - x0 > 50) f.g.append('text').attr('class', 'lbl').attr('x', (x0 + x1) / 2).attr('y', 10).attr('text-anchor', 'middle').text(`${b.days}-day break`);
    });
    const bw = Math.min(24, x.bandwidth());
    const bars = f.g.selectAll('path.b').data(weeks).join('path').attr('class', 'b')
      .attr('d', (w) => barPath(x(w.week) + (x.bandwidth() - bw) / 2, y(dist(w.km * 1000)), bw, f.ih - y(dist(w.km * 1000)), Math.min(4, bw / 3)))
      .attr('fill', css('--seq-3')).attr('opacity', (w) => (w.partial ? 0.55 : 1)).attr('tabindex', 0);
    const line = d3.line().x((w) => x(w.week) + x.bandwidth() / 2).y((w) => y(dist(w.avg4 * 1000))).curve(d3.curveMonotoneX);
    f.g.append('path').datum(weeks).attr('d', line).attr('fill', 'none').attr('stroke', css('--ink')).attr('stroke-width', 2).attr('stroke-linejoin', 'round');
    let lastM = -1;
    weeks.forEach((w) => { const d = E.fromKey(w.week), m = E.addDays(d, 3).getUTCMonth(); if (m !== lastM) { lastM = m; f.g.append('text').attr('x', x(w.week)).attr('y', f.ih + 16).text(MON[m]); } });
    f.g.append('line').attr('x1', 0).attr('x2', f.iw).attr('y1', f.ih).attr('y2', f.ih).attr('stroke', css('--rule-strong'));
    const lastA = weeks[weeks.length - 1];
    f.g.append('circle').attr('cx', x(lastA.week) + x.bandwidth() / 2).attr('cy', y(dist(lastA.avg4 * 1000))).attr('r', 4).attr('fill', css('--ink')).attr('stroke', css('--surface')).attr('stroke-width', 2);
    bindTip(bars, (w) => `Week of ${fmtDate(w.week)}${w.partial ? ' (partial week)' : ''}<br><b>${fmtDist(w.km * 1000)}</b> · ${w.runs} run${w.runs === 1 ? '' : 's'}<br>4-week average: ${fmtDist(w.avg4 * 1000)}`);
    f.svg.attr('aria-label', 'Weekly distance with 4-week rolling average');
    return { legend: `<span><span class="sw" style="background:var(--seq-3)"></span>Weekly ${du()}</span><span><span class="sw" style="background:var(--ink);height:2px;vertical-align:3px"></span>Average of the last 4 weeks</span>${weeks.some((w) => w.partial) ? '<span>Faded bars: partial weeks</span>' : ''}` };
  }

  // ---------- 3. rhythm grid ----------
  function rhythm(el, rpt) {
    const g = rpt.grid;
    let hMin = 23, hMax = 0;
    g.forEach((row) => row.forEach((c, h) => { if (c) { hMin = Math.min(hMin, h); hMax = Math.max(hMax, h); } }));
    // show at least 12 hours so grids are comparable and not cramped into a corner
    while (hMax - hMin + 1 < 12) { if (hMin > 0) hMin--; if (hMax - hMin + 1 < 12 && hMax < 23) hMax++; }
    const hours = d3.range(hMin, hMax + 1);
    const w0 = Math.max(260, el.clientWidth || 600);
    const cell = Math.max(12, Math.min(40, Math.floor((w0 - 44) / hours.length) - 3));
    const step = cell + 3;
    const f = frame(el, 7 * step + 26, { t: 4, r: 4, b: 22, l: 40 });
    const max = d3.max(g.flat()) || 1;
    const ramp = seq();
    const cells = [];
    g.forEach((row, di) => hours.forEach((h, hi) => cells.push({ di, h, hi, c: row[h] })));
    const sel = f.g.selectAll('rect').data(cells).join('rect')
      .attr('x', (d) => d.hi * step).attr('y', (d) => d.di * step).attr('width', cell).attr('height', cell).attr('rx', 3)
      .attr('fill', (d) => (d.c ? ramp[Math.min(6, 1 + Math.floor((d.c / max) * 5.999))] : css('--empty-cell'))).attr('tabindex', (d) => (d.c ? 0 : null));
    DOW.forEach((d, i) => f.g.append('text').attr('x', -8).attr('y', i * step + cell / 2).attr('dy', '0.32em').attr('text-anchor', 'end').text(d));
    hours.forEach((h, i) => { if (h % 3 === 0) f.g.append('text').attr('x', i * step + cell / 2).attr('y', 7 * step + 12).attr('text-anchor', 'middle').text(`${String(h).padStart(2, '0')}:00`); });
    bindTip(sel, (d) => `${DOW[d.di]} ${String(d.h).padStart(2, '0')}:00–${String(d.h + 1).padStart(2, '0')}:00<br><b>${d.c}</b> run${d.c === 1 ? '' : 's'}`);
    f.svg.attr('aria-label', 'Runs by weekday and start hour');
    return { legend: `<span>Start time (local)</span><span>Fewer <span class="ramp">${ramp.slice(1).map((c) => `<span style="background:${c}"></span>`).join('')}</span> more runs</span>` };
  }
  function topSlots(rpt) {
    const slots = [];
    rpt.grid.forEach((row, di) => row.forEach((c, h) => c && slots.push({ di, h, c })));
    slots.sort((a, b) => b.c - a.c);
    return slots;
  }

  // ---------- 4. long runs + jumps ----------
  function longRuns(el, rpt) {
    const f = frame(el, 250, { t: 14, r: 40, b: 26, l: 36 });
    const x = d3.scaleUtc().domain([E.fromKey(rpt.start), E.fromKey(rpt.end)]).range([0, f.iw]);
    const maxM = d3.max(rpt.runs, (r) => r.distM) || 1000;
    const y = d3.scaleLinear().domain([0, dist(maxM) * 1.12]).nice().range([f.ih, 0]);
    yGrid(f.g, y, f.iw, 4);
    // Guide: 1.1 × longest of the prior 30 days, from full history; absent wherever runs aren't checked.
    const hist = rpt.history;
    const firstHist = E.fromKey(hist[0].date);
    const backs = [];
    for (let i = 1; i < hist.length; i++) if (E.daysBetween(E.fromKey(hist[i - 1].date), E.fromKey(hist[i].date)) - 1 >= 14) backs.push(E.fromKey(hist[i].date));
    const guide = [];
    for (let d = E.fromKey(rpt.start); d <= E.fromKey(rpt.end); d = E.addDays(d, 1)) {
      const paused = E.daysBetween(firstHist, d) < 30 || backs.some((b) => d >= b && E.daysBetween(b, d) < 30);
      const L = paused ? 0 : d3.max(hist.filter((r) => { const rd = E.fromKey(r.date); return rd < d && E.daysBetween(rd, d) <= 30; }), (r) => r.distM) || 0;
      guide.push({ d, v: L ? L * 1.1 : null });
    }
    const gl = d3.line().defined((p) => p.v !== null).x((p) => x(p.d)).y((p) => y(dist(p.v))).curve(d3.curveStepAfter);
    f.g.append('path').datum(guide).attr('d', gl).attr('fill', 'none').attr('stroke', css('--ink-2')).attr('stroke-width', 1.25).attr('opacity', 0.8);
    const normal = rpt.runs.filter((r) => !(r.spike && r.spike.status === 'spike' && !r.isRace) && !r.isRace);
    const dots = f.g.selectAll('circle.r').data(normal).join('circle').attr('class', 'r')
      .attr('cx', (r) => x(E.fromKey(r.date))).attr('cy', (r) => y(dist(r.distM))).attr('r', 3.2)
      .attr('fill', css('--seq-3')).attr('opacity', 0.75).attr('tabindex', 0);
    const races = rpt.runs.filter((r) => r.isRace);
    const rd = f.g.selectAll('circle.race').data(races).join('circle').attr('class', 'race')
      .attr('cx', (r) => x(E.fromKey(r.date))).attr('cy', (r) => y(dist(r.distM))).attr('r', 6)
      .attr('fill', css('--bib')).attr('stroke', css('--ink')).attr('stroke-width', 1.5).attr('tabindex', 0);
    const sp = rpt.spikesTraining;
    const st = f.g.selectAll('path.sp').data(sp).join('path').attr('class', 'sp')
      .attr('transform', (r) => `translate(${x(E.fromKey(r.date))},${y(dist(r.distM))})`).attr('d', d3.symbol().type(d3.symbolTriangle).size(110))
      .attr('fill', css('--warn-mark')).attr('stroke', css('--surface')).attr('stroke-width', 2).attr('tabindex', 0);
    sp.forEach((r) => f.g.append('text').attr('class', 'lbl-strong').attr('x', x(E.fromKey(r.date)) + 9).attr('y', y(dist(r.distM)) + 4).text(`+${Math.round((r.spike.ratio - 1) * 100)}%`));
    monthTicks(f, x, rpt);
    f.g.append('line').attr('x1', 0).attr('x2', f.iw).attr('y1', f.ih).attr('y2', f.ih).attr('stroke', css('--rule-strong'));
    const runTip = (r) => `${fmtDate(r.date)} · ${r.isRace ? r.raceName : r.kind + ' run'}<br><b>${fmtDist(r.distM)}</b>${r.spike && r.spike.priorLongest ? `<br>Longest of the previous 30 days: ${fmtDist(r.spike.priorLongest)}` : ''}${r.spike && r.spike.status === 'after-break' ? '<br>Just after a break: not checked' : r.spike && r.spike.status === 'insufficient' ? '<br>Not enough history: not checked' : ''}`;
    bindTip(dots, runTip); bindTip(rd, runTip); bindTip(st, (r) => runTip(r) + `<br>${fmtDist(r.distM - r.spike.priorLongest)} (+${Math.round((r.spike.ratio - 1) * 100)}%) longer`);
    f.svg.attr('aria-label', 'Every run by distance, with sharp step-ups flagged');
    return { legend: `<span><span class="sw" style="background:var(--seq-3);border-radius:50%"></span>Run</span><span><span class="sw" style="background:var(--bib);border:1.5px solid var(--ink);border-radius:50%"></span>Race</span><span><span class="sw" style="background:var(--warn-mark);clip-path:polygon(50% 0,100% 100%,0 100%)"></span>More than 10% past the line</span><span><span class="sw" style="background:var(--ink-2);height:1.5px;vertical-align:3px"></span>1.1 × your longest run of the previous 30 days (gaps: not checked)</span>` };
  }

  // ---------- 5. pace histogram ----------
  function paceHist(el, rpt) {
    const f = frame(el, 210, { t: 24, r: 12, b: 34, l: 32 });
    const paces = rpt.runs.filter((r) => !r.isRace).map((r) => paceFor(r.movingS, r.distM));
    const binW = 10;
    const lo = Math.floor(d3.min(paces) / binW) * binW, hi = Math.ceil(d3.max(paces) / binW) * binW + binW;
    const bins = d3.bin().domain([lo, hi]).thresholds(d3.range(lo, hi + 1, binW))(paces);
    const x = d3.scaleLinear().domain([lo, hi]).range([0, f.iw]);
    const y = d3.scaleLinear().domain([0, d3.max(bins, (b) => b.length)]).nice().range([f.ih, 0]);
    yGrid(f.g, y, f.iw, 3);
    const bw = Math.max(2, x(lo + binW) - x(lo) - 2);
    const bars = f.g.selectAll('path').data(bins).join('path').attr('d', (b) => barPath(x(b.x0) + 1, y(b.length), bw, f.ih - y(b.length), 3)).attr('fill', css('--seq-3')).attr('tabindex', 0);
    const med = d3.median(paces);
    f.g.append('line').attr('x1', x(med)).attr('x2', x(med)).attr('y1', -4).attr('y2', f.ih).attr('stroke', css('--ink')).attr('stroke-width', 1.5);
    f.g.append('text').attr('class', 'lbl-strong').attr('x', x(med)).attr('y', -10).attr('text-anchor', x(med) > f.iw * 0.75 ? 'end' : 'start').text(`median ${E.fmtPace(med)}`);
    const nt = Math.max(3, Math.floor(f.iw / 70));
    x.ticks(nt).forEach((t) => f.g.append('text').attr('x', x(t)).attr('y', f.ih + 15).attr('text-anchor', 'middle').text(E.fmtPace(t)));
    f.g.append('text').attr('x', f.iw).attr('y', f.ih + 30).attr('text-anchor', 'end').text(`min:sec per ${du()} · faster to the left`);
    bindTip(bars, (b) => `${E.fmtPace(b.x0)}–${E.fmtPace(b.x1)} /${du()}<br><b>${b.length}</b> runs`);
    f.svg.attr('aria-label', 'Distribution of run paces');
    return { legend: '' };
  }

  // ---------- 6. zones ----------
  function zones(el, rpt) {
    const z = rpt.zoneTotals;
    const f = frame(el, 64, { t: 6, r: 4, b: 18, l: 4 });
    const ramp = seq().slice(1, 6);
    let acc = 0;
    const segs = z.map((v, i) => { const s = { i, v, x0: acc }; acc += v; return s; });
    const x = d3.scaleLinear().domain([0, 1]).range([0, f.iw]);
    const sel = f.g.selectAll('rect').data(segs).join('rect').attr('x', (s) => x(s.x0) + (s.i ? 1 : 0)).attr('y', 0)
      .attr('width', (s) => Math.max(0, x(s.v) - x(0) - (s.i ? 2 : 0))).attr('height', 34).attr('rx', 3).attr('fill', (s) => ramp[s.i]).attr('tabindex', 0);
    segs.forEach((s) => {
      if (x(s.v) - x(0) > 56) f.g.append('text').attr('x', x(s.x0 + s.v / 2)).attr('y', 21).attr('text-anchor', 'middle').style('fill', inkOn(ramp[s.i])).style('font-weight', 600).text(`Z${s.i + 1} ${Math.round(s.v * 100)}%`);
    });
    bindTip(sel, (s) => `Zone ${s.i + 1} (${['50–60', '60–70', '70–80', '80–90', '90–100'][s.i]}% of max HR)<br><b>${Math.round(s.v * 100)}%</b> of running time`);
    f.svg.attr('aria-label', 'Share of running time in each heart-rate zone');
    return { legend: z.map((v, i) => `<span><span class="sw" style="background:${ramp[i]}"></span>Z${i + 1} ${Math.round(v * 100)}%</span>`).join('') };
  }

  // ---------- 7. efficiency ----------
  function efficiency(el, rpt) {
    const f = frame(el, 210, { t: 26, r: 18, b: 26, l: 40 });
    const ms = rpt.efficiency.months;
    const x = d3.scalePoint().domain(ms.map((m) => m.key)).range([0, f.iw]).padding(0.3);
    const ext = d3.extent(ms, (m) => m.value);
    const pad = (ext[1] - ext[0]) * 0.4 || 0.05;
    const y = d3.scaleLinear().domain([ext[0] - pad, ext[1] + pad]).nice().range([f.ih, 0]);
    yGrid(f.g, y, f.iw, 4, (t) => t.toFixed(2));
    f.g.append('text').attr('x', -34).attr('y', -12).text('metres per heartbeat · axis not from 0');
    f.g.append('path').datum(ms).attr('d', d3.line().x((m) => x(m.key)).y((m) => y(m.value))).attr('fill', 'none').attr('stroke', css('--cat-1')).attr('stroke-width', 2);
    const dots = f.g.selectAll('circle').data(ms).join('circle').attr('cx', (m) => x(m.key)).attr('cy', (m) => y(m.value)).attr('r', 4.5).attr('fill', css('--cat-1')).attr('stroke', css('--surface')).attr('stroke-width', 2).attr('tabindex', 0);
    const evE = Math.max(1, Math.ceil(ms.length / Math.max(2, Math.floor(f.iw / 46))));
    ms.forEach((m, i) => { if (i % evE === 0) f.g.append('text').attr('x', x(m.key)).attr('y', f.ih + 16).attr('text-anchor', 'middle').text(m.month === 0 || i === 0 ? `${MON[m.month]} ${String(m.year).slice(2)}` : MON[m.month]); });
    bindTip(dots, (m) => `${E.MONTHS[m.month]} ${m.year}<br><b>${m.value.toFixed(3)}</b> m per heartbeat<br>median of ${m.n} easy flat runs`);
    f.svg.attr('aria-label', 'Monthly distance per heartbeat on easy flat runs');
    return { legend: '<span>Monthly median; months with fewer than 4 easy flat runs are left out</span>' };
  }

  // ---------- 8. months ----------
  function months(el, rpt) {
    const f = frame(el, 190, { t: 18, r: 6, b: 24, l: 34 });
    const ms = rpt.months;
    const x = d3.scaleBand().domain(ms.map((m) => m.key)).range([0, f.iw]).paddingInner(0.25);
    const y = d3.scaleLinear().domain([0, d3.max(ms, (m) => dist(m.km * 1000)) * 1.15 || 1]).nice().range([f.ih, 0]);
    yGrid(f.g, y, f.iw, 3);
    const bw = Math.min(24, x.bandwidth());
    const best = ms.reduce((a, m) => (m.km > a.km ? m : a), ms[0]);
    const bars = f.g.selectAll('path').data(ms).join('path').attr('d', (m) => barPath(x(m.key) + (x.bandwidth() - bw) / 2, y(dist(m.km * 1000)), bw, f.ih - y(dist(m.km * 1000)))).attr('fill', (m) => (m === best ? css('--seq-5') : css('--seq-3'))).attr('opacity', (m) => (m.partial ? 0.55 : 1)).attr('tabindex', 0);
    f.g.append('text').attr('class', 'lbl-strong').attr('x', x(best.key) + x.bandwidth() / 2).attr('y', y(dist(best.km * 1000)) - 6).attr('text-anchor', 'middle').text(Math.round(dist(best.km * 1000)));
    const long = x.bandwidth() >= 26;
    const evM = Math.max(1, Math.ceil(ms.length / Math.max(2, Math.floor(f.iw / 30))));
    ms.forEach((m, i) => { if (i % evM === 0) f.g.append('text').attr('x', x(m.key) + x.bandwidth() / 2).attr('y', f.ih + 16).attr('text-anchor', 'middle').text(long ? MON[m.month] : MON[m.month].slice(0, 1)); });
    bindTip(bars, (m) => `${E.MONTHS[m.month]} ${m.year}${m.partial ? ' (partial month)' : ''}<br><b>${fmtDist(m.km * 1000, 0)}</b> · ${m.runs} runs`);
    f.svg.attr('aria-label', 'Distance per month');
    return { legend: ms.some((m) => m.partial) ? '<span>Faded bars: partial months</span>' : '' };
  }

  // ---------- 9. source coverage ----------
  function coverageBar(el, cov) {
    const f = frame(el, 56, { t: 4, r: 4, b: 20, l: 4 });
    const parts = [{ k: 'In both exports', v: cov.both, c: css('--cat-1') }, { k: 'Strava only', v: cov.stravaOnly, c: css('--cat-2') }, { k: 'Garmin only', v: cov.garminOnly, c: css('--cat-3') }];
    const x = d3.scaleLinear().domain([0, cov.total]).range([0, f.iw]);
    let acc = 0;
    parts.forEach((p) => { p.x0 = acc; acc += p.v; });
    const sel = f.g.selectAll('rect').data(parts).join('rect').attr('x', (p, i) => x(p.x0) + (i ? 1 : 0)).attr('y', 0).attr('width', (p, i) => Math.max(2, x(p.v) - x(0) - (i ? 2 : 0))).attr('height', 24).attr('rx', 3).attr('fill', (p) => p.c).attr('tabindex', 0);
    bindTip(sel, (p) => `${p.k}<br><b>${p.v}</b> runs`);
    return { legend: parts.map((p) => `<span><span class="sw" style="background:${p.c}"></span>${p.k} · ${p.v}</span>`).join('') };
  }

  // ---------- 10. pace vs heart rate (explore) ----------
  function paceHr(el, rpt) {
    const runs = rpt.runs.filter((r) => r.avgHr && r.kind === 'easy' && !r.indoor);
    const f = frame(el, 240, { t: 14, r: 14, b: 34, l: 40 });
    const mid = runs.length ? runs[Math.floor(runs.length / 2)].date : rpt.end;
    const x = d3.scaleLinear().domain(d3.extent(runs, (r) => paceFor(r.movingS, r.distM))).nice().range([0, f.iw]);
    const y = d3.scaleLinear().domain(d3.extent(runs, (r) => r.avgHr)).nice().range([f.ih, 0]);
    yGrid(f.g, y, f.iw, 4);
    x.ticks(Math.max(3, Math.floor(f.iw / 80))).forEach((t) => f.g.append('text').attr('x', x(t)).attr('y', f.ih + 15).attr('text-anchor', 'middle').text(E.fmtPace(t)));
    f.g.append('text').attr('x', f.iw).attr('y', f.ih + 30).attr('text-anchor', 'end').text(`pace, min:sec per ${du()}`);
    f.g.append('text').attr('x', -34).attr('y', -4).text('avg HR');
    const c1 = css('--cat-1'), c2 = css('--cat-2');
    const dots = f.g.selectAll('circle').data(runs).join('circle').attr('cx', (r) => x(paceFor(r.movingS, r.distM))).attr('cy', (r) => y(r.avgHr)).attr('r', 4)
      .attr('fill', (r) => (r.date < mid ? c1 : c2)).attr('stroke', css('--surface')).attr('stroke-width', 1.5).attr('opacity', 0.85).attr('tabindex', 0);
    bindTip(dots, (r) => `${fmtDate(r.date)}<br><b>${fmtPaceU(r.movingS, r.distM)}</b> at ${r.avgHr} bpm`);
    return { legend: `<span><span class="sw" style="background:${c1};border-radius:50%"></span>Earlier half of easy runs</span><span><span class="sw" style="background:${c2};border-radius:50%"></span>Later half</span><span>Lower and further left = faster for the same effort</span>` };
  }

export default { monthStarts, monthLabel, axisMonths, UNITS, dist, du, fmtDist, fmtElev, fmtPaceU, paceFor, fmtDate, DOW, MON, CAL_BINS, calendar, weekly, rhythm, topSlots, longRuns, paceHist, zones, efficiency, months, coverageBar, paceHr, tableToggle, tip };
